/**
 * Catalog projection for the bhajan browser.
 *
 * GET /api/graph/index returns a slim node per catalog entry. /bhajans
 * re-projects those onto a FIXED allow-list of fields before filtering, which
 * keeps the working set small — but also means any field the server adds is
 * silently dropped unless it is named here, and a filter that reads it then
 * matches nothing while looking perfectly healthy.
 *
 * Extracted from Bhajans.tsx so that contract is unit-testable rather than
 * buried in a useMemo inside a component.
 */

import type { AnyGraphNode, NodeEdge } from '../types/graphNodes';

/** The raw slim-index node shape, as loosely as the wire actually types it. */
export interface CatalogNode {
  nodeId: number;
  nodeTypeId: number;
  domain?: number;
  domains?: number;
  data?: { text?: string; name?: string; normalizedName?: string };
  edges?: NodeEdge[] | Record<string, number | number[]>;
  /** Server-stamped: this bhajan's lyrics carry svara notation. */
  hasSvaras?: boolean;
}

/** Normalize either wire shape of `edges` to the canonical array form. */
function toEdgeArray(edges: CatalogNode['edges']): NodeEdge[] {
  if (Array.isArray(edges)) {
    return edges.map((e) => ({ name: e.name, nodeId: e.nodeId }));
  }
  return Object.entries(edges || {}).map(([name, nodeId]) => ({
    name,
    nodeId: typeof nodeId === 'number' ? nodeId : (nodeId as number[])[0],
  }));
}

export function projectCatalogNodes(
  nodes: CatalogNode[] | null | undefined
): AnyGraphNode[] {
  if (!nodes) return [];
  return nodes.map((n) => ({
    nodeId: n.nodeId,
    nodeTypeId: n.nodeTypeId,
    domain: n.domain,
    domains: n.domains,
    data: { name: n.data?.name, normalizedName: n.data?.normalizedName },
    edges: toEdgeArray(n.edges),
    // Node-level flags the slim index stamps. Add new ones HERE as well as to
    // flagFilter's FLAG_LABELS, or the filter will match nothing.
    ...(n.hasSvaras === true ? { hasSvaras: true as const } : {}),
  })) as AnyGraphNode[];
}
